import React, {CSSProperties} from 'react';
import {Combobox, Transition} from "@headlessui/react";
import {CheckIcon} from "@heroicons/react/solid";
import IconName from "types/IconName";
import Icon from "../../../Icon";
import {classNames, concatenate} from "services/string";

function optionKey(option: Option): string {
  if (option.type) {
    return [option.type, option.id].join('-')
  }

  return option.id?.toString() ?? ''
}

// Component ----

export type OptionId = string | number | null

export type Option = {
  id: OptionId
  uuid?: string
  name: string
  type?: string | null
  icon?: IconName | null
  description?: string | null
  unavailable?: boolean
  colour?: CSSProperties['color']
  indent?: boolean
}

type ComboboxOptionListProps = {
  options: Array<Option>
}

function ComboboxOptionList({options}: ComboboxOptionListProps) {
  return (
    <Transition
      enter="transition duration-100 ease-out"
      enterFrom="transform scale-95 opacity-0"
      enterTo="transform scale-100 opacity-100"
      leave="transition duration-75 ease-out"
      leaveFrom="transform scale-100 opacity-100"
      leaveTo="transform scale-95 opacity-0"
      className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
    >
      <Combobox.Options>
        {options.map(option => (
          <Combobox.Option
            key={optionKey(option)}
            value={option}
            disabled={option.unavailable}
            title={`${option.name}${option.description ? ` - ${option.description}` : ''}`}
            className={({active}) =>
              classNames(
                'relative cursor-default select-none py-2 pl-3 pr-9',
                active ? 'bg-brand text-white' : 'text-gray-900',
                option.unavailable && 'text-gray-400'
              )
            }
          >
            {({active, selected}) => (
              <>
                <div className="flex items-center">

                  {/* Indent arrow */}
                  {!!option.indent &&
                    <span className="ml-2 mr-2">↳</span>
                  }

                  {/* Option Icon */}
                  {option.icon ?
                    <Icon
                      name={option.icon}
                      className="h-6 w-6 flex-shrink-0"
                      color={active ? '#ffffff' : ((!option.unavailable && option.colour) || '#333333')}
                    />
                    :
                    <span
                      className={'inline-block h-2 w-2 flex-shrink-0 rounded-full bg-gray-200'}
                      style={{backgroundColor: (!option.unavailable && option.colour) || undefined}}
                      aria-hidden="true"
                    />
                  }

                  {/* Option Name */}
                  <span className={classNames('ml-3 block truncate', selected && 'font-semibold')}>
                      {option.name}
                  </span>

                  {/* Option description */}
                  {option.description &&
                    <span
                      className={concatenate(
                        'ml-4 sm:ml-2 truncate md:inline',
                        active ? 'text-gray-200' : 'text-gray-500'
                      )}
                    >
                        {option.description}
                    </span>
                  }
                </div>

                {selected && (
                  <span
                    className={classNames(
                      'absolute inset-y-0 right-0 flex items-center pr-4',
                      active ? 'text-white' : 'text-brand'
                    )}
                  >
                    <CheckIcon className="h-5 w-5" aria-hidden="true"/>
                  </span>
                )}

              </>
            )}
          </Combobox.Option>
        ))}
      </Combobox.Options>
    </Transition>
  );
}

export default ComboboxOptionList;